import type { Ref } from 'react';
import { createContext } from 'react';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import type {
	MenuItemProps as AriaMenuItemProps,
	MenuProps as AriaMenuProps,
	MenuTriggerProps,
	SubmenuTriggerProps,
} from 'react-aria-components/Menu';
import { Menu as AriaMenu, MenuItem as AriaMenuItem, MenuTrigger, SubmenuTrigger } from 'react-aria-components/Menu';
import type { ContextValue } from 'react-aria-components/slots';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

import { Icon } from '@launchpad-ui/icons';

import { CheckboxIcon, checkboxStyles } from './Checkbox';
import { useLPContextProps } from './utils';

import styles from './styles/Menu.module.css';

const menuStyles = cva(styles.menu);
const menuItemStyles = cva(styles.item, {
	variants: {
		variant: {
			default: styles.default,
			destructive: styles.destructive,
		},
	},
	defaultVariants: {
		variant: 'default',
	},
});

interface MenuProps<T> extends AriaMenuProps<T> {
	ref?: Ref<HTMLDivElement>;
}
interface MenuItemProps<T> extends AriaMenuItemProps<T>, VariantProps<typeof menuItemStyles> {
	/**
	 * `destructive` marks an action that deletes or cannot be undone. Use it for the action itself,
	 * not for a menu that merely contains one.
	 * @default 'default'
	 */
	variant?: VariantProps<typeof menuItemStyles>['variant'];
	/**
	 * Where a single-selection item puts its check. Use `end` when the item already leads with a
	 * visual element of its own, such as an icon or a chip, and `none` when selection is conveyed by
	 * the item's own styling, as in a menu that acts as navigation.
	 * @default 'start'
	 */
	selectionIndicatorPlacement?: 'start' | 'end' | 'none';
	ref?: Ref<HTMLDivElement>;
}

// react-aria-components types this identically: `MenuContext: React.Context<ContextValue<MenuProps<any>, HTMLDivElement>>`
// (react-aria-components/dist/types/src/Menu.d.ts) — a context can't carry the open generic `T`, so RAC itself erases it to `any` here.
// oxlint-disable-next-line typescript/no-explicit-any -- mirrors react-aria-components' own MenuContext declaration (see comment above)
const MenuContext = createContext<ContextValue<MenuProps<any>, HTMLDivElement>>(null);

/**
 * A menu displays a list of actions or options that a user can choose, opened from a trigger.
 *
 * Reach for something else when the user is choosing a form value (`Select`, or `ComboBox` when the
 * list needs filtering), when the options are always visible (`ListBox`), or when the surface holds
 * arbitrary content rather than a list of actions (`Popover` with your own children).
 *
 * A menu is always composed as `MenuTrigger` → trigger → `Popover` → `Menu`, and needs an
 * `aria-label` unless something else already names it. Group related items with `MenuSection` plus
 * a `Header`, divide them with `Separator`, and nest a `SubmenuTrigger` for a submenu. Handle
 * presses with `onAction` on the menu rather than `onPress` on each item.
 *
 * Selection is opt-in via `selectionMode`: `single` marks the chosen item and closes on select,
 * `multiple` renders a checkbox per item and stays open.
 *
 * https://react-spectrum.adobe.com/react-aria/Menu.html
 *
 * @example
 * ```tsx
 * <MenuTrigger>
 *   <Button variant="picker">Actions</Button>
 *   <Popover>
 *     <Menu aria-label="Actions" onAction={run}>
 *       <MenuItem id="edit">Edit</MenuItem>
 *       <Separator />
 *       <MenuItem id="delete" variant="destructive">Delete</MenuItem>
 *     </Menu>
 *   </Popover>
 * </MenuTrigger>
 * ```
 */
const Menu = <T extends object>({ ref, ...props }: MenuProps<T>) => {
	const [mergedProps, mergedRef] = useLPContextProps(props, ref, MenuContext);
	return (
		<AriaMenu
			{...mergedProps}
			ref={mergedRef}
			className={composeRenderProps(mergedProps.className, (className, renderProps) =>
				menuStyles({ ...renderProps, className }),
			)}
		/>
	);
};

/**
 * A MenuItem represents an individual action in a Menu.
 *
 * Give it an `id` so `onAction` and `disabledKeys` on the parent `Menu` can address it. The item
 * renders its own selection indicator and submenu chevron, so don't add either by hand.
 *
 * In a single-selection menu the check leads the item and unselected items reserve the same space,
 * so labels stay aligned. An item that leads with its own icon or chip can move the check to the
 * other side with `selectionIndicatorPlacement="end"`, and an item that shouldn't show one at all
 * can opt out with `selectionIndicatorPlacement="none"`.
 */
const MenuItem = <T extends object>({
	variant = 'default',
	selectionIndicatorPlacement = 'start',
	ref,
	...props
}: MenuItemProps<T>) => {
	return (
		<AriaMenuItem
			{...props}
			ref={ref}
			className={composeRenderProps(props.className, (className, renderProps) =>
				menuItemStyles({ ...renderProps, variant, className }),
			)}
		>
			{composeRenderProps(props.children, (children, { selectionMode, isSelected, hasSubmenu, isDisabled }) => {
				const selectionIndicator = selectionMode === 'single' && (
					<span aria-hidden className={styles.selectionIndicator}>
						{isSelected && <Icon name="check" size="small" />}
					</span>
				);

				return (
					<>
						{selectionMode === 'multiple' && (
							<div
								className={checkboxStyles({ className: styles.checkbox })}
								data-selected={isSelected || undefined}
								data-disabled={isDisabled || undefined}
							>
								<CheckboxIcon isSelected={isSelected} />
							</div>
						)}
						{selectionIndicatorPlacement === 'start' && selectionIndicator}
						<span className={styles.content}>{children}</span>
						{selectionIndicatorPlacement === 'end' && selectionIndicator}
						{hasSubmenu && <Icon name="chevron-right" size="small" />}
					</>
				);
			})}
		</AriaMenuItem>
	);
};

export { Menu, MenuContext, MenuItem, MenuTrigger, SubmenuTrigger, menuItemStyles, menuStyles };
export type { MenuProps, MenuItemProps, MenuTriggerProps, SubmenuTriggerProps };
